
import React from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode | ((props: {error: Error; resetError: () => void}) => React.ReactNode);
  name?: string;
};

const DefaultFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => (
  <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 m-4">
    <div className="flex items-center gap-3 mb-4">
      <AlertTriangle className="text-red-500 h-6 w-6" />
      <h2 className="text-lg font-semibold text-red-700 dark:text-red-300">Something went wrong</h2>
    </div>
    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
      The application encountered an error. Our team has been notified.
    </p>
    <div className="mt-4 flex flex-col sm:flex-row gap-2">
      <Button 
        variant="outline" 
        onClick={resetError}
        className="border-red-300 hover:bg-red-100 dark:border-red-700 dark:hover:bg-red-900/30"
      >
        Try again
      </Button>
      <Button 
        variant="link" 
        onClick={() => window.location.href = '/'}
        className="text-red-600 dark:text-red-400"
      >
        Return to home
      </Button>
    </div>
  </div>
);

const ErrorBoundary = ({ children, fallback, name }: ErrorBoundaryProps) => {
  // Transform our fallback renderer to match Sentry's expected FallbackRender type
  const renderFallback: Sentry.FallbackRender = (errorData) => {
    const { error, resetError } = errorData;
    
    if (typeof fallback === 'function') {
      return fallback({ 
        error: error as Error, 
        resetError 
      }) as React.ReactElement;
    }
    
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return <DefaultFallback error={error as Error} resetError={resetError} />;
  };

  return (
    <Sentry.ErrorBoundary
      fallback={renderFallback}
      beforeCapture={(scope) => {
        scope.setTag('boundary', name || 'unnamed');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
